/**
 * BuildCards - Sub-component for BuildApkPage
 *
 * Renders the Flutter APK card and React Native APK card with build controls.
 */
import React from 'react';
import { Smartphone, Package, Download, Loader, CheckCircle, Info } from 'lucide-react';

interface FlutterCardProps {
  building: boolean;
  status: 'idle' | 'building' | 'success' | 'error';
  output: string;
  onBuild: () => void;
  onDownload: () => void;
}

const FlutterApkCard: React.FC<FlutterCardProps> = ({ building, status, output, onBuild, onDownload }) => (
  <div className="bg-white rounded-xl shadow-xl border-2 border-gray-200">
    <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-4">
      <div className="flex items-center gap-2">
        <Smartphone className="w-6 h-6 text-white" />
        <h2 className="text-xl font-bold text-white">Flutter APK</h2>
      </div>
      <p className="text-blue-100 mt-1 text-sm">
        Nativo com IP auto-detectado (ZeroTier/Local)
      </p>
    </div>

    <div className="p-4">
      {status === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-green-800 font-semibold text-sm">APK gerado!</span>
          </div>
        </div>
      )}

      <button
        onClick={onBuild}
        disabled={building}
        className={`w-full py-3 rounded-lg font-semibold text-white transition-all mb-3 ${
          building ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {building ? (
          <div className="flex items-center justify-center gap-2">
            <Loader className="w-4 h-4 animate-spin" />
            <span>Gerando...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <Package className="w-4 h-4" />
            <span>Gerar APK Flutter</span>
          </div>
        )}
      </button>

      {status === 'success' && (
        <button
          onClick={onDownload}
          className="w-full py-3 rounded-lg font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 transition-all border-2 border-blue-200"
        >
          <div className="flex items-center justify-center gap-2">
            <Download className="w-4 h-4" />
            <span>Baixar logmed_flutter.apk</span>
          </div>
        </button>
      )}

      {output && (
        <div className="bg-gray-900 rounded-lg p-3 mt-3">
          <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap max-h-64 overflow-y-auto">
            {output}
          </pre>
        </div>
      )}
    </div>
  </div>
);

interface ReactCardProps {
  building: boolean;
  status: 'idle' | 'building' | 'success' | 'error';
  progress: number;
  output: string;
  onBuild: () => void;
  onDownload: () => void;
}

const ReactNativeApkCard: React.FC<ReactCardProps> = ({ building, status, progress, output, onBuild, onDownload }) => (
  <div className="bg-white rounded-xl shadow-xl border-2 border-gray-200">
    <div className="bg-gradient-to-r from-purple-600 to-purple-500 p-4">
      <div className="flex items-center gap-2">
        <Smartphone className="w-6 h-6 text-white" />
        <h2 className="text-xl font-bold text-white">React Native APK</h2>
      </div>
      <p className="text-purple-100 mt-1 text-sm">
        Híbrido com Capacitor e IP auto-detectado
      </p>
    </div>

    <div className="p-4">
      {status === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-green-800 font-semibold text-sm">APK gerado!</span>
          </div>
        </div>
      )}

      <button
        onClick={onBuild}
        disabled={building}
        className={`w-full py-3 rounded-lg font-semibold text-white transition-all mb-3 ${
          building ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'
        }`}
      >
        {building ? (
          <div className="flex items-center justify-center gap-2">
            <Loader className="w-4 h-4 animate-spin" />
            <span>Gerando... {progress}%</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <Package className="w-4 h-4" />
            <span>Gerar APK React Native</span>
          </div>
        )}
      </button>

      {/* Progress Bar */}
      {building && (
        <div className="mb-3">
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500 ease-out flex items-center justify-end"
              style={{ width: `${progress}%` }}
            >
              <span className="text-xs text-white font-bold pr-2">{progress}%</span>
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-1 text-center">
            {progress < 20 && 'Preparando ambiente...'}
            {progress >= 20 && progress < 40 && 'Instalando dependências...'}
            {progress >= 40 && progress < 60 && 'Configurando Capacitor...'}
            {progress >= 60 && progress < 80 && 'Compilando com Gradle...'}
            {progress >= 80 && progress < 100 && 'Gerando APK...'}
            {progress === 100 && 'Concluído!'}
          </p>
        </div>
      )}

      {status === 'success' && (
        <button
          onClick={onDownload}
          className="w-full py-3 rounded-lg font-semibold text-purple-600 bg-purple-50 hover:bg-purple-100 transition-all border-2 border-purple-200"
        >
          <div className="flex items-center justify-center gap-2">
            <Download className="w-4 h-4" />
            <span>Baixar logmed_react.apk</span>
          </div>
        </button>
      )}

      {output && (
        <div className="bg-gray-900 rounded-lg p-3 mt-3">
          <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap max-h-64 overflow-y-auto">
            {output}
          </pre>
        </div>
      )}
    </div>
  </div>
);

interface BuildCardsProps {
  flutter: FlutterCardProps;
  react: ReactCardProps;
}

export const BuildCards: React.FC<BuildCardsProps> = ({ flutter, react }) => {
  return (
    <div className="lg:col-span-2 space-y-6">
      {/* Info Box */}
      <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <h3 className="font-semibold text-blue-900 mb-1">Informações:</h3>
            <ul className="text-blue-800 space-y-0.5 list-disc list-inside">
              <li>APK detecta IP automaticamente (ZeroTier prioritário)</li>
              <li>Celular deve estar na mesma rede (ZeroTier ou WiFi)</li>
              <li>Build demora 3-5 minutos</li>
            </ul>
          </div>
        </div>
      </div>

      <FlutterApkCard {...flutter} />
      <ReactNativeApkCard {...react} />
    </div>
  );
};
